<template>
    <section v-if="isLoading || (!isLoading && data.length > 0)" class="tw-flex tw-flex-row tw-mb-[30px]">
        <div class="tw-flex tw-flex-col tw-w-full">
            <!-- Section Title -->
            <div :id="sectionId" class="tw-flex tw-items-center tw-mb-4 tw-w-full tw-justify-between">
                <div class="tw-flex tw-items-center">
                    <component :is="seeAllUrl ? 'a' : 'div'" @click="handleSeeAllClick" :href="seeAllUrl"
                       class="tw-flex tw-items-center tw-text-[#00101D] dark:tw-text-white tw-pb-1 tw-border-b tw-border-transparent tw-transition-all " :class="seeAllUrl ? 'hover:tw-border-current' : ''">
                        <h2 class="tw-font-bold tw-text-[20px] tw-leading-[30px] lg:tw-leading-[36px] lg:tw-text-[24px]" v-html="title"></h2>
                        <ChevronRightIcon v-if="seeAllUrl" class="tw-w-5" />
                    </component>
                    <slot name="label"></slot>
                </div>
                <div v-if="showPagination" class="tw-flex tw-items-center">
                    <div class="tw-hidden lg:tw-flex">
                        <button class="tw-w-[30px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-rounded-full tw-border tw-border-[#B2B2B5] dark:tw-border-[#223F57] tw-bg-white dark:tw-bg-[#081825] tw-text-[#000C17] dark:tw-text-white hover:tw-bg-[#000C17] hover:tw-border-[#000C17] hover:tw-text-white dark:hover:tw-bg-[#223F57] disabled:tw-bg-[#F4F4F5] disabled:hover:tw-border-[#B2B2B5] disabled:hover:tw-bg-white dark:disabled:hover:tw-bg-[#081825] disabled:tw-text-[#B2B2B5] dark:disabled:tw-text-[#223F57] dark:disabled:tw-border-[#223F57] tw-mr-[10px]" :disabled="isFirstPage" @click="prevPage"><ChevronLeftIcon class="tw-w-[20px] tw-h-[20px]"  /></button>
                        <button class="tw-w-[30px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-rounded-full tw-border tw-border-[#B2B2B5] dark:tw-border-[#223F57] tw-bg-white dark:tw-bg-[#081825] tw-text-[#000C17] dark:tw-text-white hover:tw-bg-[#000C17] hover:tw-border-[#000C17] hover:tw-text-white dark:hover:tw-bg-[#223F57] disabled:tw-bg-[#F4F4F5] disabled:hover:tw-border-[#B2B2B5] disabled:hover:tw-bg-white dark:disabled:hover:tw-bg-[#081825] disabled:tw-text-[#B2B2B5] dark:disabled:tw-text-[#223F57] dark:disabled:tw-border-[#223F57]" :disabled="isLastPage" @click="nextPage"><ChevronRightIcon class="tw-w-[20px] tw-h-[20px]" /></button>
                    </div>
                </div>
            </div>
            <slot name="tabs" :resetPagination="resetPagination"></slot>
            <div class="tw-overflow-hidden">
                <transition appear name="fade">
                    <ChallengeCarousel v-if="isChallengeCarousel" :section-title="sectionTitle" :pre-loaded-content="data" :page-type="pageType" @remove-challenge="removeItem" @re-fetch-carousel="reFetchData" @handle-scroll-end="handleScrollEnd" @activate-auto-scroll="activateAutoScroll" @stop-auto-scroll="clearAutoScroll"  />
                    <ChallengeCardContainer v-else-if="isChallenge" :section-title="sectionTitle" :content="data" />
                    <ChallengeAwardContainer v-else-if="isChallengeAward" :section-title="sectionTitle" :pre-loaded-content="data" />
                    <PackCatalogue v-else-if="isPack" :section-title="sectionTitle" :content="data" />
                    <CatalogueCardContainer
                        v-else
                        :section-title="sectionTitle"
                        :force-no-links="forceNoLinks"
                        :is-mini-view="isMiniView"
                        :pre-loaded-content="data"
                        :show-dropdown="showDropdown"
                        :tracking-section="trackingSection"
                        :page="page"
                        :is-mini-catalogue="true"
                        :show-see-all-card="preLoadedContent.length > 6 && showSeeAllCard"
                        @on-progress-reset="resetProgress"
                        @handle-scroll-end="handleScrollEnd"
                    />
                </transition>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from "pinia/dist/pinia";
import { usePlatformStore } from "@stores/platform";
import { useUserStore } from '@stores/user';
import userJourney from '@services/userJourney';
import { getCardNum } from '@collections/MiniCatalogueSection/getCardNum';
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/solid";
import useCarouselEvents from "@hooks/useCarouselEvents";

import CatalogueCardContainer from '@collections/Catalogue/CatalogueCardContainer';
import ChallengeCarousel from '@collections/ChallengeCarousel/ChallengeCarousel';
import ChallengeAwardContainer from '@collections/ChallengeAwardContainer/ChallengeAwardContainer';
import ChallengeCardContainer from '@collections/Catalogue/ChallengeCardContainer';
import PackCatalogue from "@collections/Packs/PackCatalogue";

const props = defineProps({
  seeAllUrl: {
    type: String,
    default: ''
  },
  contentEndpoint: {
    type: String,
    default: '/railcontent/content'
  },
  preLoadedContent: {
    type: Array,
    default: [],
  },
  title: {
    type: String,
    default: ''
  },
  isMiniView: {
    type: Boolean,
    default: false
  },
  forceNoLinks: {
    type: Boolean,
    default: false
  },
  seeAllAriaLabel: {
    type: String,
    default: ''
  },
  showDropdown: {
    type: Boolean,
    default: () => false,
  },
  useRefData: {
    type: Boolean,
    default: () => false,
  },
  trackCardClick: {
    type: Boolean,
    default: () => false,
  },
  trackingSection: {
    type: String,
    default: ''
  },
  catalogueType: {
    type: String,
    default: ''
  },
  pageType: {
    type: String,
    default: ''
  },
  sectionId: {
    type: String,
    default: ''
  },
  autoScroll: {
    type: Boolean,
    default: false,
  },
});

const userStore = useUserStore();
const platformStore = usePlatformStore();
const { isLoading } = storeToRefs(platformStore);

const data = ref([]);
const page = ref(1);
const cardNum = ref(5);
const showSeeAllCard = ref(false);

const isChallengeCarousel = computed(() => {
    return props.catalogueType === 'challenge-carousel';
})

const isChallenge = computed(() => {
    return props.catalogueType === 'challenge';
})

const isPack = computed(() => {
    return props.catalogueType === 'pack';
})

const isChallengeAward = computed(() => {
    return props.catalogueType === 'challengeAward';
})

const sectionTitle = computed(() => {
    if(isChallengeCarousel.value){
        return 'challengeCarousel';
    }
    return props.title.replace(/\s/g, ' ').toLowerCase();
})

const initialData = computed(() => {
    return props.preLoadedContent;
})

const handleSeeAllClick = (event) => {
  if (props.seeAllUrl && props.trackingSection) {
    event.preventDefault();

    userJourney.trackHomeSeeAll({
      payload: {
        brand: userStore.brand,
        section: props.trackingSection,
      }
    }).finally(() => {
      window.location.href = props.seeAllUrl;
    });
  }
};

const toggleSeeAllCard = (val) => {
    showSeeAllCard.value = val;
}

const watchResize = () => {
    getCardNum(props, cardNum, toggleSeeAllCard);
    if(props.isMiniView) data.value = reformatData(props.preLoadedContent)

    toStartIndex();
}

onMounted(() => {
    watchResize();
    window.addEventListener('resize', watchResize);
    if(props.autoScroll && data.value.length > cardNum.value){
        activateAutoScroll();
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', watchResize);
    if(props.autoScroll){
        clearAutoScroll();
    }
})

watch(
    () => props.preLoadedContent,
    (newData) => {
        toStartIndex();
    },
)

const { showPagination, isFirstPage, isLastPage, getPageData, resetProgress, nextPage, prevPage, removeItem, reFetchData, resetPagination, toStartIndex, activateAutoScroll, clearAutoScroll, handleScrollEnd, reformatData } = useCarouselEvents(initialData, data, page, cardNum, sectionTitle, props.autoScroll, props.isMiniView, isChallengeCarousel);
</script>
